"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { fetchJson } from "@/lib/api";

function isPublicRoute(pathname: string) {
  return pathname === "/" || pathname === "/login" || pathname === "/signup" || pathname === "/onboarding" || pathname === "/password-reset" || pathname === "/email-change" || pathname === "/terms" || pathname === "/privacy" || pathname.startsWith("/invite/");
}

export function AuthGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isPublic = isPublicRoute(pathname);
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  // Any API that reports an expired session funnels here so the whole app
  // returns to the login screen instead of rendering an inline notice.
  useEffect(() => {
    function onAuthRequired(event: Event) {
      // Public screens (login, signup, landing) legitimately run while
      // unauthenticated; redirecting there would loop.
      if (isPublicRoute(window.location.pathname)) return;
      const next = (event as CustomEvent<{ next?: string }>).detail?.next ?? `${window.location.pathname}${window.location.search}`;
      // A single ambiguous answer must not end an editing session: confirm the
      // session is really gone (three explicit answers) before leaving.
      void (async () => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 1200 : 1500));
          try {
            const data = await fetchJson<{ authenticated: boolean }>("/api/auth/status");
            if (data.authenticated) return;
          } catch {
            return;
          }
        }
        router.replace(`/login?next=${encodeURIComponent(next)}`);
      })();
    }
    window.addEventListener("tairoh-auth-required", onAuthRequired);
    return () => window.removeEventListener("tairoh-auth-required", onAuthRequired);
  }, [router]);

  // Path in a ref so the mount-only status check can still make redirect
  // decisions without re-running on every navigation.
  const pathnameRef = useRef(pathname);
  useEffect(() => { pathnameRef.current = pathname; }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    let failures = 0;
    // A status check can fail because the local server is restarting or a
    // request was cancelled. Keep the current page and retry instead of
    // treating an unanswered check as a signed-out user.
    const check = async () => {
      if (cancelled) return;
      try {
        const data = await fetchJson<{ authenticated: boolean }>("/api/auth/status", undefined, { silent: true });
        if (cancelled) return;
        setAuthed(data.authenticated);
        setReady(true);
      } catch {
        if (cancelled) return;
        failures += 1;
        window.setTimeout(check, Math.min(10_000, 500 * failures));
      }
    };
    void check();
    // Re-check when the tab becomes visible again (session may have changed
    // elsewhere). Navigation itself does not need a new check: the RSC request
    // already validates the session server-side.
    function onVisible() {
      if (document.visibilityState === "visible") void check();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Signed-in users landing on a public screen go to the app home.
  useEffect(() => {
    if (!ready || !authed || !isPublic) return;
    const current = pathnameRef.current;
    if (current === "/onboarding" || current === "/email-change" || current.startsWith("/invite/")) return;
    router.replace("/app/all/home");
  }, [authed, isPublic, ready, router]);

  useEffect(() => {
    if (!ready || authed || isPublic) return;
    let cancelled = false;
    let attempts = 0;
    // Only a completed check that explicitly reports an unauthenticated session
    // may sign the user out, and it has to agree several times in a row. A
    // failed check never redirects: the editor the user is working in stays.
    const verify = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const again = await fetchJson<{ authenticated: boolean }>("/api/auth/status");
        if (cancelled) return;
        if (again.authenticated) { setAuthed(true); return; }
        if (attempts < 3) { window.setTimeout(verify, 1500); return; }
        const next = `${window.location.pathname}${window.location.search}`;
        router.replace(`/login?next=${encodeURIComponent(next)}`);
      } catch {
        if (!cancelled) window.setTimeout(verify, Math.min(10_000, 1000 * attempts));
      }
    };
    const timer = window.setTimeout(verify, 1500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [authed, isPublic, ready, router]);

  if (isPublic) return <>{children}</>;
  if (!ready) return <>{children}</>;
  if (!authed) {
    return (
      <main className="mx-auto w-full max-w-xl p-6">
        <div className="surface-panel p-6 text-center">
          <h1 className="text-lg font-semibold">ログインが必要です</h1>
          <p className="mt-2 text-sm text-[color:var(--muted)]">ログイン画面へ移動しています…</p>
          <Link href={`/login?next=${encodeURIComponent(typeof window === "undefined" ? "" : window.location.pathname + window.location.search)}`} className="button-primary mt-4 inline-flex h-10 items-center rounded-md px-4 text-sm font-semibold">ログイン画面へ</Link>
        </div>
      </main>
    );
  }
  return <>{children}</>;
}
